import type { MicState } from '@/lib/mic/useMicController';
import './MicButton.css';

/**
 * Shared mic button used by RoleplayScreen's controls and AskCoachInput.
 * Visual state matches the controller's MicState exactly so a single tap can
 * be reasoned about end-to-end.
 *
 * Sizes:
 *   "md" — default, used in the role-play controls row.
 *   "sm" — compact, used in AskCoachInput.
 */

export interface MicButtonProps {
  state: MicState;
  onTap: () => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /**
   * Conversation mode: the mic is an always-on continuous listener, so the
   * button is a single "listening" indicator rather than a per-turn toggle.
   */
  continuous?: boolean;
}

export default function MicButton({
  state,
  onTap,
  disabled,
  size = 'md',
  continuous = false,
}: MicButtonProps) {
  const label = continuous
    ? state === 'error'
      ? 'Microphone unavailable — tap for help'
      : state === 'listening'
        ? 'Listening — just talk'
        : 'Connecting the mic…'
    : state === 'listening'
      ? 'Listening — tap to stop'
      : state === 'committing'
        ? 'Submitting…'
        : state === 'error'
          ? 'Microphone unavailable — tap for help'
          : 'Tap to speak';

  // In conversation mode the loader auto-manages the mic, so show the "live"
  // (listening) treatment whenever it isn't an error — even during the brief
  // committing window — so the indicator doesn't flicker between turns.
  const visualState: MicState = continuous && state !== 'error' ? 'listening' : state;

  return (
    <button
      type="button"
      className={`mic-btn mic-btn--${size} mic-btn--${visualState}${continuous ? ' mic-btn--continuous' : ''}`}
      aria-label={label}
      aria-pressed={visualState === 'listening'}
      aria-live={continuous ? 'polite' : undefined}
      title={label}
      disabled={disabled}
      onClick={onTap}
    >
      <span className="mic-btn__halo" aria-hidden />
      <span className="mic-btn__core" aria-hidden>
        {state === 'committing' ? <Spinner /> : state === 'error' ? <Warning /> : <MicIcon />}
      </span>
    </button>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="M5 11a7 7 0 0 0 14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Spinner() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden className="mic-btn__spinner">
      <circle
        cx="12"
        cy="12"
        r="8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="14 36"
      />
    </svg>
  );
}

function Warning() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 4 2.5 20h19L12 4Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path d="M12 10v5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="12" cy="17.5" r="1" fill="currentColor" />
    </svg>
  );
}
